import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Row, Col } from "react-bootstrap";
import {
  getState,
  organizerListClubsApi,
  organizerGetClubApi,
  organizerUpdateClubApi,
  organizerDeleteClubApi,
  organizerToggleClubApi,
} from "../../api/api";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import { handleError } from "../../helpers/handleError";
import { OrganizerHeaderContext } from "../Layout";
import { MAIN_API } from "../../api/apilist";
import { SPORT_LABELS } from "../../constants/courtDomain";
import { borrarBorrador } from "../recordatorios/borradorPoliticas";
import ClubForm, { emptyClubForm, normalizeClubForForm, buildClubFormData } from "./ClubForm";
import CanchasGuidedFlow from "./primerUso/CanchasGuidedFlow";
import useMultiSede from "../hooks/useMultiSede";
import OrgReservationsScreen from "../reservas/components/OrgReservationsScreen";
import { useVolverAPoliticas } from "../recordatorios/volverAPoliticas";
import "../reservas/reservas-organizer.css";

/* ─────────────────────────────────────────
   CanchasOrganizer — mirrors the shell pattern of
   src/organizer/reservas/ReservasOrganizer.jsx (list <-> form routing,
   OrgToast, ConfirmModal), pointed at the dedicated /api/organizer/clubs/*
   endpoints (task 5.1/5.2). Reservation management reuses the shared
   OrgReservationsScreen (extracted from ReservasOrganizer.jsx in Unit 7 /
   PR7, task 7.2) with `variant="club"` (task 7.3/7.4), which projects
   courtName/sport/durationMinutes instead of the bar's zone/partySize and
   reuses the same organizer reservation API functions
   (organizerListReservationsApi/organizerConfirmReservationApi/
   organizerCancelReservationApi/organizerNoShowReservationApi) with the
   club's `_id` passed as `barId` — same `bars` collection (Club is a Bar
   discriminator), so no backend change was needed.

   Sesión 6 (instrucSesiones del repo de Zoco): un negocio tiene como máximo
   un club, así que la pantalla de inicio ya no es una lista para elegir —
   ver ClubScreen.
───────────────────────────────────────── */

const resolveUrl = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (/^blob:/i.test(u)) return u;
  if (u.startsWith("/")) return `${MAIN_API}${u}`;
  return `${MAIN_API}/${u}`;
};

const FALLBACK_IMAGE = "https://via.placeholder.com/400x130?text=Club";

function getCleanErrorMessage(error) {
  if (!error) return "";
  if (error.response?.data) {
    const data = error.response.data;
    if (typeof data === "string") return data;
    if (data.message) {
      let msg = data.message;
      if (data.errors) {
        if (Array.isArray(data.errors)) {
          msg += "\n" + data.errors.map((e) => (typeof e === "string" ? e : e.message || JSON.stringify(e))).join("\n");
        } else if (typeof data.errors === "object") {
          msg += "\n" + Object.entries(data.errors).map(([k, v]) => `- ${k}: ${v}`).join("\n");
        }
      }
      return msg;
    }
  }
  return error.message || "Ocurrió un error inesperado. Intenta nuevamente.";
}

function OrgToast({ message, onClose }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [message, onClose]);

  if (!message) return null;
  return (
    <div className="org-toast" role="alert">
      {message}
    </div>
  );
}

function ConfirmModal({ clubName, onConfirm, onCancel }) {
  return (
    <div className="zr-overlay" onMouseDown={onCancel}>
      <div className="zr-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="zr-modal__icon">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="currentColor" style={{ color: "var(--zr-lime)" }}>
            <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.5 5.5a1.5 1.5 0 0 1 3 0v6a1.5 1.5 0 0 1-3 0zM12 18.4a1.7 1.7 0 1 1 0-3.4 1.7 1.7 0 0 1 0 3.4z" />
          </svg>
        </div>
        <h3 className="zr-modal__title">¿Eliminar club?</h3>
        <p className="zr-modal__text">
          Se eliminará <strong>{clubName}</strong>. Esta acción no se puede deshacer.
        </p>
        <div className="zr-modal__actions">
          <button type="button" className="zr-btn zr-btn--ghost" onClick={onCancel}>Cancelar</button>
          <button type="button" className="zr-btn zr-btn--dark" style={{ minWidth: 160, height: 46 }} onClick={onConfirm}>
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
}

function ClubCardItem({ club, onEdit, onDelete, onToggleStatus, onShowReservations, onCopyPublicLink, getCityName, getStateName }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  const city = getCityName(club);
  const state = getStateName(club);
  const locationText = [club.address, city, state].filter(Boolean).join(" • ");
  const isActive = club.isEnabled !== false;
  const coverUrl = club.imageUrl ? resolveUrl(club.imageUrl) : club.image ? resolveUrl(club.image) : FALLBACK_IMAGE;
  const sports = Array.isArray(club.sports) ? club.sports : [];

  return (
    <Col xs={12} md={6} xl={4}>
      <div className="zr-barcard" style={menuOpen ? { zIndex: 10 } : {}}>
        <div className="zr-barcard__cover" style={{ backgroundImage: `url(${coverUrl})` }}>
          <span className={`zr-barcard__status ${isActive ? "zr-barcard__status--active" : "zr-barcard__status--inactive"}`}>
            <span className="dot" />
            {isActive ? "Activo" : "Inactivo"}
          </span>
        </div>
        <div className="zr-barcard__body">
          <div className="zr-barcard__titlerow">
            <h3 className="zr-barcard__name">{club.name}</h3>
            <div ref={menuRef} style={{ position: "relative" }}>
              <button type="button" className="zr-barcard__menu" onClick={() => setMenuOpen((v) => !v)} aria-label="Acciones">
                •••
              </button>
              {menuOpen && (
                <div className="zr-cardmenu">
                  <button type="button" onClick={() => { setMenuOpen(false); onEdit(club._id); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16.5 4.5 19.5 7.5 8 19l-3.5.5L5 16z" /><path d="M14.5 6.5 17.5 9.5" /></svg>
                    Editar
                  </button>
                  <button type="button" onClick={() => { setMenuOpen(false); onDelete(club); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6.5h16M9 6.5V4.5h6v2M6.5 6.5 7.5 20a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1l1-13.5" /></svg>
                    Eliminar
                  </button>
                  <button type="button" onClick={() => { setMenuOpen(false); onCopyPublicLink(club); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                    Copiar enlace
                  </button>
                  <button type="button" onClick={() => { setMenuOpen(false); onToggleStatus(club); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>
                    {isActive ? "Desactivar" : "Activar"}
                  </button>
                </div>
              )}
            </div>
          </div>
          {locationText && (
            <p className="zr-barcard__addr">
              <svg width="14" height="17" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 2C8.1 2 5 5.1 5 9c0 5 7 13 7 13s7-8 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" /></svg>
              {locationText}
            </p>
          )}
          {sports.length > 0 && (
            <p className="zr-barcard__addr">
              {sports.slice(0, 3).map((s) => SPORT_LABELS[s] || s).join(" • ")}
              {sports.length > 3 ? ` +${sports.length - 3}` : ""}
            </p>
          )}
          <div className="zr-barcard__stats">
            <span className="zr-stat">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M6.2 6.2c2.4 2.4 2.4 6.4 0 8.8" />
                <path d="M17.8 6.2c-2.4 2.4-2.4 6.4 0 8.8" />
              </svg>
              <span className="zr-stat__txt">
                <b>{String(club.sports?.length ?? 0).padStart(2, "0")}</b>
                <span>Deportes</span>
              </span>
            </span>
            <span className="zr-stat">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <span className="zr-stat__txt">
                <b>{String(club.tablesCount ?? 0).padStart(2, "0")}</b>
                <span>Canchas</span>
              </span>
            </span>
            <span className="zr-stat">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 20L7 6h11l3 14H10z" /><path d="M7 6l3 14" /><path d="M12 10h5" /><path d="M13 13h5" /><path d="M14 16h5" /></svg>
              <span className="zr-stat__txt">
                <span>
                  <b>{String(club.reservationsCount ?? 0).padStart(2, "0")}</b> Reservas
                </span>
                <span>Próximas</span>
              </span>
            </span>
          </div>
          <div className="zr-barcard__footer">
            <button type="button" className="zr-btn zr-btn--lime zr-btn--block" onClick={() => onShowReservations(club)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><path d="m9 16 2 2 4-4" /></svg>
              Ver Reservas
            </button>
            <button type="button" className="zr-btn zr-btn--dark zr-btn--block" onClick={() => onEdit(club._id, "canchas")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              Ver Canchas
            </button>
          </div>
        </div>
      </div>
    </Col>
  );
}

/*
  El club del negocio. Un negocio tiene como máximo un club (sesión 5: el back responde 409 al
  segundo, y lo sostiene el índice un_club_por_negocio de model/Bar.js), así que esto no es una
  lista para elegir: muestra el club, o la invitación a crearlo si el negocio todavía no tiene.
  Por eso no hay buscador, ni forma de crear otro club: uno nuevo es un negocio nuevo en el portal.
  La excepción son los negocios multi-sede (`multiSede` en el perfil): ellos sí ven "Crear club",
  que reabre el flujo guiado para sumar otro.
  El primero se arma con el flujo guiado (ver CanchasGuidedFlow).

  Se pinta todo lo que venga, sin quedarse con el primero: un organizador de los de antes, que
  todavía no está vinculado a ningún negocio de Zoco, puede tener dos clubes activos de cuando la
  regla no existía, y esconder uno lo dejaría sin forma de gestionarlo.
*/
// Un club borrado no cuenta: el borrado es lógico, y un negocio que borró el suyo puede configurar otro.
const clubsActivos = (clubs) => clubs.filter((c) => c.status !== false);
const sinClub = (clubs) => clubsActivos(clubs).length === 0;

function ClubScreen({ clubs, states, loading, multiSede, onEmpezar, onEdit, onDelete, onToggleStatus, onShowReservations, onCopyPublicLink }) {
  const activeClubs = clubsActivos(clubs);

  // The organizer API populates cityid with its name, so no national city catalog is needed here.
  const getCityName = (club) => club.cityid?.name || "";
  const getStateName = (club) => states.find((s) => String(s._id) === String(club.stateid?._id || club.stateid))?.name || "";

  return (
    <div>
      {loading && <SpinLoading />}

      {!loading && activeClubs.length === 0 && (
        <div className="zr-panel" style={{ padding: 0 }}>
          <div className="zr-empty" style={{ flexDirection: "column", gap: 12, textAlign: "center", padding: 24 }}>
            <p style={{ margin: 0 }}>Este negocio todavía no tiene club.</p>
            <p style={{ margin: 0 }}>Configuralo para cargar sus canchas y sus horarios, y empezar a recibir reservas.</p>
            <button type="button" className="zr-btn zr-btn--dark" onClick={onEmpezar}>
              Empezar configuración
            </button>
          </div>
        </div>
      )}

      {!loading && multiSede && activeClubs.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <button type="button" className="zr-btn zr-btn--dark" onClick={onEmpezar}>
            Crear club
          </button>
        </div>
      )}

      {!loading && activeClubs.length > 0 && (
        <Row className="zr-bars-grid">
          {activeClubs.map((club) => (
            <ClubCardItem
              key={club._id}
              club={club}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleStatus={onToggleStatus}
              onShowReservations={onShowReservations}
              onCopyPublicLink={onCopyPublicLink}
              getCityName={getCityName}
              getStateName={getStateName}
            />
          ))}
        </Row>
      )}
    </div>
  );
}

const CanchasOrganizer = () => {
  const { setPageHeader } = useContext(OrganizerHeaderContext);
  const multiSede = useMultiSede();

  useEffect(() => {
    setPageHeader({
      title: "Reservas de canchas",
      subtitle: "El club de tu negocio: canchas, horarios y reservas.",
    });
  }, [setPageHeader]);

  const [route, setRoute] = useState("list"); // "list" | "form" | "reservations"
  const [selectedClub, setSelectedClub] = useState(null);
  // First run: the negocio has no club yet, so the guided flow replaces the empty list.
  const [guiado, setGuiado] = useState(false);
  const [seccionInicial, setSeccionInicial] = useState("info");

  const [clubs, setClubs] = useState([]);
  const [states, setStates] = useState([]);

  const [clubForm, setClubForm] = useState(emptyClubForm());

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageError, setPageError] = useState("");
  const [formError, setFormError] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [confirmClub, setConfirmClub] = useState(null);

  const loadLocationCatalog = useCallback(async () => {
    try {
      const statesRes = await getState();
      setStates(Array.isArray(statesRes?.data?.info) ? statesRes.data.info : []);
    } catch (_) { }
  }, []);

  const loadClubs = useCallback(async () => {
    const response = await organizerListClubsApi();
    const list = Array.isArray(response?.data?.info) ? response.data.info : [];
    setClubs(list);
    return list;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setPageError("");
        const [list] = await Promise.all([loadClubs(), loadLocationCatalog()]);
        setGuiado(sinClub(list));
      } catch (err) {
        setPageError(err?.response?.data?.message || "No pudimos cargar el módulo de canchas.");
      } finally {
        setLoading(false);
      }
    })();
  }, [loadClubs, loadLocationCatalog]);

  const handleEdit = async (clubId, initialTab = "info") => {
    try {
      setLoading(true);
      setFormError("");
      const res = await organizerGetClubApi(clubId);
      const info = res?.data?.info || {};
      setClubForm(normalizeClubForForm(info));
      setSeccionInicial(initialTab);
      setRoute("form");
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  // Coming back from Zoco Chat (templates): reopen the club's form on Políticas.
  useVolverAPoliticas({ listo: !loading, locales: clubs, abrir: (club) => handleEdit(club._id, "politicas") });

  const handleDeleteRequest = (club) => setConfirmClub(club);

  const handleDeleteConfirm = async () => {
    if (!confirmClub) return;
    try {
      setSaving(true);
      await organizerDeleteClubApi(confirmClub._id);
      await loadClubs();
      setToastMessage(`"${confirmClub.name}" eliminado correctamente.`);
    } catch (err) {
      handleError(err);
    } finally {
      setSaving(false);
      setConfirmClub(null);
    }
  };

  const handleToggleStatus = async (club) => {
    try {
      setSaving(true);
      const res = await organizerToggleClubApi(club._id);
      const isEnabled = res?.data?.info?.isEnabled;
      await loadClubs();
      setToastMessage(isEnabled === false ? `"${club.name}" desactivado.` : `"${club.name}" activado.`);
    } catch (err) {
      handleError(err);
    } finally {
      setSaving(false);
    }
  };

  // El club solo se edita acá: crearlo es cosa del flujo guiado (un club por negocio).
  const handleSave = async () => {
    setFormError("");
    try {
      setSaving(true);
      await organizerUpdateClubApi(buildClubFormData(clubForm));
      borrarBorrador(clubForm.clubId);
      await loadClubs();
      setToastMessage("Club actualizado correctamente.");
      setFormError("");
      setRoute("list");
    } catch (err) {
      setFormError(getCleanErrorMessage(err));
      handleError(err);
    } finally {
      setSaving(false);
    }
  };

  // Guided first run finished: back to the module, or straight to the form section left pending.
  const handleTerminarGuiado = async (ctx, sectionId) => {
    setGuiado(false);
    try {
      await loadClubs();
    } catch (err) {
      handleError(err);
    }
    if (ctx?.id && sectionId) await handleEdit(ctx.id, sectionId);
  };

  // Leaving the flow after the club was already created: show it instead of the empty invitation.
  const handleCancelarGuiado = async (clubCreado) => {
    setGuiado(false);
    if (!clubCreado) return;
    try {
      await loadClubs();
    } catch (err) {
      handleError(err);
    }
  };

  if (loading && !clubs.length && !pageError) return <SpinLoading />;

  return (
    <div className="zr-app zr-main">
      {pageError && (
        <div className="org-page-error">
          <p>{pageError}</p>
          <button type="button" onClick={() => window.location.reload()}>Reintentar</button>
        </div>
      )}

      {route === "list" && guiado && !loading && (
        <CanchasGuidedFlow states={states} onTerminar={handleTerminarGuiado} onCancelar={handleCancelarGuiado} />
      )}

      {route === "list" && !guiado && (
        <ClubScreen
          clubs={clubs}
          states={states}
          loading={loading}
          multiSede={multiSede}
          onEmpezar={() => setGuiado(true)}
          onEdit={handleEdit}
          onDelete={handleDeleteRequest}
          onToggleStatus={handleToggleStatus}
          onShowReservations={(club) => {
            setSelectedClub(club);
            setRoute("reservations");
          }}
          onCopyPublicLink={async (club) => {
            const url = `${window.location.origin}/canchas/${club._id}`;
            try {
              if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
              } else {
                const textArea = document.createElement("textarea");
                textArea.value = url;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
              }
              setToastMessage("Enlace copiado al portapapeles.");
            } catch (err) {
              setToastMessage("No se pudo copiar el enlace.");
            }
          }}
        />
      )}

      {route === "reservations" && selectedClub && (
        <OrgReservationsScreen
          bar={selectedClub}
          variant="club"
          onBack={() => {
            setRoute("list");
            setSelectedClub(null);
          }}
          setToastMessage={setToastMessage}
        />
      )}

      {route === "form" && (
        <ClubForm
          clubForm={clubForm}
          setClubForm={setClubForm}
          states={states}
          saving={saving}
          onSave={handleSave}
          initialSection={seccionInicial}
          onCancel={() => {
            setRoute("list");
            setFormError("");
          }}
          formError={formError}
        />
      )}

      {confirmClub && (
        <ConfirmModal clubName={confirmClub.name} onConfirm={handleDeleteConfirm} onCancel={() => setConfirmClub(null)} />
      )}

      <OrgToast message={toastMessage} onClose={() => setToastMessage("")} />
    </div>
  );
};

export default CanchasOrganizer;
